import { Serializable, JsonProperty } from 'ts-jackson'
import { Fromable } from '../model'

export enum RoomStatusFilter {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  UNDER_MAINTENANCE = 'UNDER_MAINTENANCE',
  INACTIVE = 'INACTIVE',
}

export enum RoomTypeFilter {
  ALL = 'ALL',
  GROUP_CLASS = 'GROUP_CLASS',
  PRIVATE_SESSION = 'PRIVATE_SESSION',
  RECOVERY_RELAXATION = 'RECOVERY_RELAXATION',
}

@Serializable()
export class RoomListData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('type')
  type!: string

  @JsonProperty('capacity')
  capacity!: number

  @JsonProperty('status')
  status!: string

  @JsonProperty('description')
  description!: string
}

Serializable()
export class CreateRoomPayload extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('branchId')
  branchId?: number

  @JsonProperty('type')
  type!: string

  @JsonProperty('capacity')
  capacity!: number

  @JsonProperty('status')
  status!: string

  @JsonProperty('openTime')
  openTime!: string

  @JsonProperty('closeTime')
  closeTime!: string

  @JsonProperty('description')
  description?: string | null

  @JsonProperty('bookingLeadTimeMinutes')
  bookingLeadTimeMinutes?: number

  @JsonProperty('cancelCutoffMinutes')
  cancelCutoffMinutes?: number
}

@Serializable()
export class RoomDetailData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('occupiedCount')
  occupiedCount!: number

  @JsonProperty('type')
  type!: string

  @JsonProperty('capacity')
  capacity!: number

  @JsonProperty('status')
  status!: string

  @JsonProperty('description')
  description!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('bookingLeadTimeMinutes')
  bookingLeadTimeMinutes!: number

  @JsonProperty('cancelCutoffMinutes')
  cancelCutoffMinutes!: number

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty('deletedAt')
  deletedAt!: string

  @JsonProperty('openingHours')
  openingHours!: RoomOpeningHours
}

@Serializable()
export class RoomOpeningHours extends Fromable {
  @JsonProperty('roomId')
  roomId!: string

  @JsonProperty('monIsClosed')
  monIsClosed!: boolean

  @JsonProperty('monOpenTime')
  monOpenTime!: string

  @JsonProperty('monCloseTime')
  monCloseTime!: string

  @JsonProperty('tueIsClosed')
  tueIsClosed!: boolean

  @JsonProperty('tueOpenTime')
  tueOpenTime!: string

  @JsonProperty('tueCloseTime')
  tueCloseTime!: string

  @JsonProperty('wedIsClosed')
  wedIsClosed!: boolean

  @JsonProperty('wedOpenTime')
  wedOpenTime!: string

  @JsonProperty('wedCloseTime')
  wedCloseTime!: string

  @JsonProperty('thuIsClosed')
  thuIsClosed!: boolean

  @JsonProperty('thuOpenTime')
  thuOpenTime!: string

  @JsonProperty('thuCloseTime')
  thuCloseTime!: string

  @JsonProperty('friIsClosed')
  friIsClosed!: boolean

  @JsonProperty('friOpenTime')
  friOpenTime!: string

  @JsonProperty('friCloseTime')
  friCloseTime!: string

  @JsonProperty('satIsClosed')
  satIsClosed!: boolean

  @JsonProperty('satOpenTime')
  satOpenTime!: string

  @JsonProperty('satCloseTime')
  satCloseTime!: string

  @JsonProperty('sunIsClosed')
  sunIsClosed!: boolean

  @JsonProperty('sunOpenTime')
  sunOpenTime!: string

  @JsonProperty('sunCloseTime')
  sunCloseTime!: string

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string
}

@Serializable()
export class RoomBookedData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('roomId')
  roomId!: string

  @JsonProperty('startAt')
  startAt!: string

  @JsonProperty('endAt')
  endAt!: string

  @JsonProperty('reason')
  reason!: any

  @JsonProperty('status')
  status!: string

  /**
   * Non-null when this booking backs a PT (private training) session.
   * Recent BE addition — optional so older/cached responses that omit the
   * field still deserialize.
   */
  @JsonProperty('ptBookingId')
  ptBookingId?: number | null

  @JsonProperty('room')
  room!: RoomListData

  @JsonProperty('createdByUser')
  createdByUser!: RoomUserData

  @JsonProperty('cancelledByUser')
  cancelledByUser!: RoomUserData

  @JsonProperty('cancelledAt')
  cancelledAt!: string

  @JsonProperty('instructors')
  instructors!: RoomUserData[]

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty('classSchedules')
  classSchedules!: classScheduleData[]
}

/** What a cell of a room's seat map is. BLOCKED cells exist but can never be booked. */
export enum ROOM_SPOT_KIND {
  SEAT = 'SEAT',
  INSTRUCTOR = 'INSTRUCTOR',
  BLOCKED = 'BLOCKED',
}

/**
 * One cell of a room's seat map. Coordinates are 1-based (row 1 / col 1 =
 * top-left). Plain type on purpose: ts-jackson nested arrays arrive as plain
 * objects under Vite, so nothing here may rely on being a class instance.
 */
export type RoomSpot = {
  id: string
  label: string
  row: number
  col: number
  kind: ROOM_SPOT_KIND
  isActive: boolean
  sort: number
}

/** Response of `GET /rooms/:id/layout`. `rows`/`cols` are null until a map exists. */
@Serializable()
export class RoomLayout extends Fromable {
  @JsonProperty('requiresSpotSelection')
  requiresSpotSelection!: boolean

  @JsonProperty('rows')
  rows!: number | null

  @JsonProperty('cols')
  cols!: number | null

  @JsonProperty('spots')
  spots!: RoomSpot[]
}

/** One cell in a `PUT /rooms/:id/layout` body. `id` is omitted for new cells. */
export type RoomSpotPayload = {
  id?: string
  label: string
  row: number
  col: number
  kind: ROOM_SPOT_KIND
  isActive?: boolean
  sort?: number
}

/**
 * Body of `PUT /rooms/:id/layout` — the whole grid, replacing what was there.
 * `force: true` is the deliberate second attempt after the backend refused with
 * 409 because a removed/changed seat is still held by a future booking; it nulls
 * those bookings' spot.
 */
export type UpdateRoomLayoutPayload = {
  requiresSpotSelection: boolean
  rows: number | null
  cols: number | null
  spots: RoomSpotPayload[]
  force?: boolean
}

export type CreateRoomBookingPayload = {
  roomId: string | number
  startAt: string
  endAt: string
  reason?: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  instructorUserIds: Array<string | number>
  isPrivateSession?: boolean
  memberId?: number
  serviceId?: number
}

@Serializable()
export class RoomUserData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('imageUrl')
  imageUrl!: string

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string
}

/** Nested class relation on a schedule entry — only `title` is consumed client-side. */
export interface ClassScheduleClassInfo {
  title?: string
}

@Serializable()
export class classScheduleData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('classId')
  classId!: number

  /**
   * Class relation with its title. Recent BE addition — optional so older/
   * cached responses that only carry { id, classId } still deserialize.
   */
  @JsonProperty('class')
  class?: ClassScheduleClassInfo
}
